import gql from 'graphql-tag';

// GraphQL Query for MDM Settings Page
const GET_MDM_CLIENT_CONFIG_LIST = gql`
  query getMDMClientConfigList(
    $listInfo: ListInfoInput!
    $pendingSetup: Boolean
  ) {
    getMDMClientConfigList(listInfo: $listInfo, pendingSetup: $pendingSetup) {
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
      clients {
        client {
          accountId
          name
        }
        androidMDMClientConfig {
          id
          name
          addedBy {
            userId
            name
          }
          createdAt
          updatedAt
          expiresAt
          expiresInDays
          status
        }
        appleMDMClientConfig {
          id
          name
          addedBy {
            userId
            name
          }
          createdAt
          updatedAt
          expiresAt
          expiresInDays
          status
        }
      }
    }
  }
`;

// GraphQL Query for Pending Clients (for ClientSelectionModal)
const GET_PENDING_CLIENTS = gql`
  query getMDMClientConfigList(
    $listInfo: ListInfoInput!
    $pendingSetup: Boolean
  ) {
    getMDMClientConfigList(listInfo: $listInfo, pendingSetup: $pendingSetup) {
      clients {
        client {
          accountId
          name
          accountType
        }
      }
    }
  }
`;

// GraphQL Query for getting client-specific MDM config
const GET_MDM_CLIENT_CONFIG_FOR_CLIENT = gql`
  query getMDMClientConfigForClient($clientId: ID) {
    getMDMClientConfigForClient(clientId: $clientId) {
      client {
        accountId
        name
      }
      androidMDMClientConfig {
        id
        name
        addedBy {
          userId
          name
        }
        createdAt
        updatedAt
        expiresAt
        expiresInDays
        status
      }
      appleMDMClientConfig {
        id
        name
        addedBy {
          userId
          name
        }
        appleId
        createdAt
        updatedAt
        expiresAt
        expiresInDays
        status
      }
      appleADEConnectionConfig {
        adeId
        expiresInDays
        expiryDate
        status
        appleId
        lastSyncTime
        assetSyncStatus
      }
    }
  }
`;

const GET_MDM_CLIENT_CONFIG_FOR_CLIENT_FOR_SYNC = gql`
  query getMDMClientConfigForClient($clientId: ID) {
    getMDMClientConfigForClient(clientId: $clientId) {
      appleADEConnectionConfig {
        assetSyncStatus
      }
    }
  }
`;

// Android Enterprise Setup Mutation
const SETUP_ANDROID_ENTERPRISE = gql`
  mutation setupAndroidEnterpriseAccountForClient($clientId: ID) {
    setupAndroidEnterpriseAccountForClient(clientId: $clientId) {
      redirectURL
    }
  }
`;

// Client Sites Query
const GET_CLIENT_SITE_LIST = gql`
  query getClientSiteList($input: ListInfoInput) {
    getClientSiteList(input: $input) {
      sites {
        id
        name
        timezoneCode
        working24x7
        contactNumber
        account {
          accountId
          name
        }
        hq
      }
    }
  }
`;

// Apple APNS Certificate Setup Mutation
const SETUP_APPLE_APNS_CERTIFICATE = gql`
  mutation setupAppleAPNSCertificateForClient($clientId: ID) {
    setupAppleAPNSCertificateForClient(clientId: $clientId) {
      csrId
    }
  }
`;

// Apple APNS Certificate Renew Mutation
const RENEW_APPLE_APNS_CERTIFICATE = gql`
  mutation renewAppleAPNSCertificateForClient($clientId: ID) {
    renewAppleAPNSCertificateForClient(clientId: $clientId) {
      csrId
      apnsId
    }
  }
`;

// MDM Enrollment QR Generation Mutation
const GENERATE_MDM_ENROLLMENT_QR = gql`
  mutation generateMDMEnrollmentQR($input: MDMEnrollmentInput!) {
    generateMDMEnrollmentQR(input: $input) {
      enrollmentUrl
      enrollmentCode
      qrCode
      enrollmentConfig
      validationCode
    }
  }
`;

// Send MDM Enrollment QR Via Email Mutation
const SEND_MDM_ENROLLMENT_QR_VIA_EMAIL = gql`
  mutation sendMDMEnrollmentQRViaEmail(
    $input: MDMEnrollmentInput!
    $emailIds: [String]
  ) {
    sendMDMEnrollmentQRViaEmail(input: $input, emailIds: $emailIds)
  }
`;

// Get MDM Device Enrollment Data Query
const GET_MDM_DEVICE_ENROLLMENT_DATA = gql`
  mutation getMDMDeviceEnrollmentData($input: MDMEnrollmentInput!) {
    getMDMDeviceEnrollmentData(input: $input) {
      enrollmentUrl
      enrollmentCode
      # qrCode
      enrollmentConfig
    }
  }
`;

// Apple ADE Connection Setup Mutation
const SETUP_APPLE_ADE_CONNECTION = gql`
  mutation setupAppleADEConnectionForClient($clientId: ID!) {
    setupAppleADEConnectionForClient(clientId: $clientId) {
      id
      adeId
    }
  }
`;

// Get Apple ADE Assets Query
const GET_APPLE_ADE_ASSETS = gql`
  query getAppleADEAssets($adeId: ID!, $listInfo: ListInfoInput!, $isMSP: Boolean!) {
    getAppleADEAssets(adeId: $adeId, listInfo: $listInfo) {
      assets {
        id
        serialNumber
        color
        model
        description
        enrollmentStatus
        policyCategory {
          policyCategoryId
          name
        }
        client @include(if: $isMSP) {
          accountId
          name
        }
        site {
          id
          name
        }
        department {
          businessFunctionId
          name
        }
        owner {
          userId
          name
        }
        assetClass {
          classId
          name
        }
        enrolledAsset {
          assetId
          monitoringStartTime
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
        sort {
          attribute
          order
        }
      }
    }
  }
`;

// Get Policy Set Mapping Query
const GET_POLICY_SET_MAPPING = gql`
  query getPolicySetMapping($clientId: ID, $module: String, $classId: ID) {
    getPolicySetMapping(
      clientId: $clientId
      module: $module
      classId: $classId
    ) {
      policySet {
        policySetId
        name
        assetClass {
          classId
          supportedModules
        }
      }
      policyCategory {
        policyCategoryId
        name
        assetClass {
          classId
          name
          iconClass
        }
        custom
      }
    }
  }
`;

// Get Entities Meta Query
const GET_ENTITIES_META = gql`
  query getEntitiesMeta($entitiesName: [String]) {
    getEntitiesMeta(entityNames: $entitiesName) {
      label
      name
      attributes {
        name
        label
        attributePlaceHolder {
          key
          label
        }
        filterType
        isIdentifier
        isKeyAttribute
        isDependent
        dataType
        entity
        uiDataType
        conditionPlaceHolders {
          key
          label
        }
        isCustom
        nestedAttributes
        nestedTo
        showToClient
        autoComplete
        useInMetric
        metricFunctions
        metricUnits
        labelInMetric
        useInGroupBy
        useInCondition
        conditionPlaceHoldersMeta
      }
    }
  }
`;

// Get Apple ADE Asset Meta Query
const GET_APPLE_ADE_ASSET_META = gql`
  query getAppleADEAssetMeta($clientId: ID) {
    getAppleADEAssetMeta(clientId: $clientId)
  }
`;

// Start Apple ADE Asset Sync Mutation
const START_ADE_ASSET_SYNC = gql`
  mutation startADEAssetSync($adeId: ID!) {
    startADEAssetSync(adeId: $adeId)
  }
`;

// Update Apple ADE Assets Enrollment Status Mutation
const UPDATE_APPLE_ADE_ASSETS_ENROLLMENT_STATUS = gql`
  mutation updateAppleADEAssetsEnrollmentStatus(
    $appleADEAssetIds: [ID!]
    $enrollmentStatus: String!
  ) {
    updateAppleADEAssetsEnrollmentStatus(
      appleADEAssetIds: $appleADEAssetIds
      enrollmentStatus: $enrollmentStatus
    )
  }
`;

const UPDATE_APPLE_ADE_ASSETS = gql`
  mutation updateAppleADEAssets($input: [AppleADEAssetInput]) {
    updateAppleADEAssets(updateADEAssetInput: $input)
  }
`;

const GET_VPP_APP_DETAILS = gql`
  query getVPPAppDetails($vppConfigId: ID, $listInfo: ListInfoInput) {
    getVPPAppDetails(vppConfigId: $vppConfigId, listInfo: $listInfo) {
      apps {
        id
        adamId
        title
        author
        iconUrl
        applicationUrl
        supportedAssetClass {
          classId
          name
        }
        totalLicense
        availableLicense
        # bundleId
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_VPP_CONFIG = gql`
  query getVPPConfig($clientId: ID!, $listInfo: ListInfoInput) {
    getVPPConfig(clientId: $clientId, listInfo: $listInfo) {
      vppConfig {
        id
        name
        allSite
        sites {
          id
          name
        }
        email
        expiryDate
        expiryInDays
        lastSyncTime
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_VPP_CONFIG_BY_VPP_ID = gql`
  query getVPPConfigByVPPId($vppId: ID!) {
    getVPPConfigByVPPId(vppId: $vppId) {
      id
      name
      allSite
      sites {
        id
        name
      }
      email
      expiryDate
      expiryInDays
      lastSyncTime
    }
  }
`;

const UPDATE_VPP_CONFIG = gql`
  mutation updateVPPConfig($input: UpdateVPPConfigInput!) {
    updateVPPConfig(input: $input)
  }
`;

const START_VPP_SYNC = gql`
  mutation startVPPSync($configId: ID!) {
    startVPPSync(configId: $configId)
  }
`;

// Get Apple Software Inventory Query
const GET_APPLE_SOFTWARE_INVENTORY_QUERY = gql`
  query getAppleSoftwareInventory($assetId: ID!, $listInfo: ListInfoInput!) {
    getAppleSoftwareInventory(assetId: $assetId, listInfo: $listInfo) {
      apps {
        id
        name
        identifier
        shortVersion
        volumePurchased
        updateAvailable
        betaApp
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;


const GET_UN_MAPPED_VPP_SITE_LIST = gql`
  query getUnMappedVPPSiteList($clientId: ID) {
    getUnMappedVPPSiteList(clientId:$clientId) {
      sites {
        id
        name
      }
    }
  }
`;

export {
  GET_MDM_CLIENT_CONFIG_LIST,
  GET_PENDING_CLIENTS,
  GET_MDM_CLIENT_CONFIG_FOR_CLIENT,
  SETUP_ANDROID_ENTERPRISE,
  GET_CLIENT_SITE_LIST,
  SETUP_APPLE_APNS_CERTIFICATE,
  RENEW_APPLE_APNS_CERTIFICATE,
  SETUP_APPLE_ADE_CONNECTION,
  GET_APPLE_ADE_ASSETS,
  GENERATE_MDM_ENROLLMENT_QR,
  SEND_MDM_ENROLLMENT_QR_VIA_EMAIL,
  GET_MDM_DEVICE_ENROLLMENT_DATA,
  GET_POLICY_SET_MAPPING,
  GET_ENTITIES_META,
  GET_APPLE_ADE_ASSET_META,
  START_ADE_ASSET_SYNC,
  UPDATE_APPLE_ADE_ASSETS_ENROLLMENT_STATUS,
  GET_MDM_CLIENT_CONFIG_FOR_CLIENT_FOR_SYNC,
  UPDATE_APPLE_ADE_ASSETS,
  GET_VPP_APP_DETAILS,
  GET_VPP_CONFIG,
  GET_VPP_CONFIG_BY_VPP_ID,
  UPDATE_VPP_CONFIG,
  START_VPP_SYNC,
  GET_APPLE_SOFTWARE_INVENTORY_QUERY,
  GET_UN_MAPPED_VPP_SITE_LIST
};
